// ─── NARRATOR WHISPERS — "Siren's Whisper" feature ─────────────────────────
//
// When one user is spectating the other's RP, they can submit story-guidance
// suggestions ("whispers") that get folded into the next AI response's
// system prompt, then marked consumed so they only steer one beat. Themed
// as a siren-style guiding voice — invisible to the player in their UI,
// audible to the AI.

import { supabase } from './supabaseClient.js';

/**
 * Submit a new whisper from spectator → story owner.
 */
export async function submitWhisper({ storyId, fromUserId, toUserId, text }) {
  if (!storyId || !fromUserId || !toUserId || !text?.trim()) {
    return { error: { message: 'Missing required fields' } };
  }
  return supabase.from('narrator_whispers').insert({
    story_id: storyId,
    from_user_id: fromUserId,
    to_user_id: toUserId,
    whisper_text: text.trim().slice(0, 1500),
  });
}

/**
 * List whispers I've sent for a story (for the spectator's own UI).
 * Includes consumed status so they can see what's been used.
 */
export async function listMyWhispersForStory(storyId, fromUserId) {
  if (!storyId || !fromUserId) return [];
  try {
    const { data } = await supabase
      .from('narrator_whispers')
      .select('id, whisper_text, consumed, consumed_at, created_at')
      .eq('story_id', storyId)
      .eq('from_user_id', fromUserId)
      .order('created_at', { ascending: false })
      .limit(50);
    return data || [];
  } catch (err) {
    console.error('[whispers] listMyWhispersForStory error:', err);
    return [];
  }
}

/**
 * Fetch unconsumed whispers TO this user for this story. Called at
 * prompt-build time on the recipient's side.
 *
 * Uses the SECURITY DEFINER RPC fetch_unconsumed_whispers_for_recipient
 * because the SELECT RLS policy is sender-only — recipients can't read
 * the table directly (so the owner can never query and SEE what
 * spectators are whispering, only experience the AI's interpretation).
 * The RPC enforces auth.uid() = recipient internally.
 */
export async function getUnconsumedWhispers(storyId, toUserId) {
  if (!storyId || !toUserId) {
    console.warn('[whispers] getUnconsumedWhispers: missing storyId or toUserId', { storyId, toUserId });
    return [];
  }
  try {
    const { data, error } = await supabase
      .rpc('fetch_unconsumed_whispers_for_recipient', { p_story_id: storyId });
    if (error) {
      console.warn('[whispers] RPC error:', error.message, { storyId });
      return [];
    }
    // Loud success log so we can verify in browser console that whispers
    // are reaching prompt-build time. Also helps diagnose silent miss cases.
    if ((data || []).length > 0) {
      console.log(`[whispers] ✨ Loaded ${data.length} pending whisper(s) into next prompt`,
        data.map(w => ({ id: w.id, preview: String(w.whisper_text || '').slice(0, 60) })));
    }
    return data || [];
  } catch (err) {
    console.error('[whispers] getUnconsumedWhispers error:', err);
    return [];
  }
}

/**
 * Format whispers as a prefix for the next user message. This belt-and-suspenders
 * the system-prompt placement — at 30K+ tokens, system-prompt instructions can
 * get partially ignored, but a directive at the head of the user turn is
 * essentially impossible for the model to miss.
 */
export function formatWhispersForUserPrefix(whispers) {
  if (!whispers || whispers.length === 0) return '';
  const lines = whispers.map((w, i) => `${i + 1}. ${w.whisper_text}`);
  return `[HIDDEN NARRATOR DIRECTION — out-of-frame story guidance from a second writer behind the curtain. The player you're responding to does NOT see this; only you do. Treat it as authorial direction for THIS response (NOT in-character speech, NOT something a character heard). Weave it into your reply naturally, in story, not as an announcement. If multiple notes, fold them all in:\n${lines.join('\n')}]\n\n`;
}

/**
 * Mark a list of whispers as consumed. Called after the AI response that
 * used them lands successfully.
 */
export async function markWhispersConsumed(ids) {
  if (!Array.isArray(ids) || ids.length === 0) return;
  try {
    await supabase
      .from('narrator_whispers')
      .update({ consumed: true, consumed_at: new Date().toISOString() })
      .in('id', ids);
  } catch (err) {
    console.error('[whispers] markWhispersConsumed error:', err);
  }
}

/**
 * Delete a whisper (sender-only, before it's consumed).
 */
export async function deleteWhisper(id) {
  if (!id) return;
  return supabase.from('narrator_whispers').delete().eq('id', id);
}

/**
 * Format whispers into a system-prompt section.
 */
export function formatWhispersForPrompt(whispers) {
  if (!whispers || whispers.length === 0) return '';
  const lines = whispers.map((w, i) => `${i + 1}. ${w.whisper_text}`);
  return `\n══════════════════════════════════════════════════════════════════════════
🌊 SIREN'S WHISPER — HIGHEST-PRIORITY DIRECTION FOR THIS RESPONSE 🌊
══════════════════════════════════════════════════════════════════════════
A second writer (a co-author for this story, behind the curtain) is shaping THIS specific beat. Their notes below override your default instincts about what the scene should do next. You MUST execute their direction in this response — not the next one, not approximately, THIS response.

Rules:
- Treat as AUTHORIAL DIRECTION, not in-character speech and not something any character heard.
- Fold the direction in naturally, in story — make it land as scene, not as an announcement.
- If a whisper says "have X happen," X must happen in this response.
- If a whisper specifies dialogue, use that dialogue (or very close to it).
- If a whisper requests a specific reveal/beat/decision, deliver it in this response.
- The player you are responding to does NOT see these notes; only you do.

WHISPERS FOR THIS BEAT:
${lines.join('\n')}
══════════════════════════════════════════════════════════════════════════
`;
}
